import { I18n } from '@calendly/i18n';
import { IconApps } from '@calendly/ui/components/glyphs/IconApps';
import { IconList } from '@calendly/ui/components/glyphs/IconList';
import { IconSettings } from '@calendly/ui/components/glyphs/IconSettings';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { ViewSwitcher } from '@calendly/ui/components/view-switcher';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { MeetingsFilter } from 'components/meetings';
import { AllSchedulingMethods, FilterTypes } from 'constants/meetings';
import { useNotetakerEntitled } from 'features/meeting_intelligence';
import { SETTINGS_SECTION } from 'features/settings_modal/constants';
import { SETTINGS_MODAL_AI_TAB } from 'features/settings_modal/settings_modal_ai_page';
import { useSettingsNavigation } from 'features/settings_modal/use_settings_navigation';
import { NOTETAKER_SETTINGS_PATH } from 'shared/constants/personal_browser_urls';
import { useGetMeetingRecapsQuery } from './graphql_api';
import { createUseResultsCount } from './hooks/use_results_count';
import { PaywallBanner } from './paywalls/paywall_banner';
import { MEETINGS_RECAP_SCOPE, RecapScopeTabs } from './recap_scope_tabs';
import { SearchInput } from './search';
const headerContainerHideFilterCss = "hkuxppa";
const paywallBannerRowCss = "p1lcmzis";
const recapScopeTabsWrapCss = "r1vdaxlj";
const headerContainerCss = "h1mqdnsv";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    hideFilter,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, {
    [headerContainerHideFilterCss]: hideFilter
  }, props.className)} />;
});
const rowIsPhoneCss = "r8ngp2p";
const rowCss = "r1jfqbgi";
const Row = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(rowCss, {
    [rowIsPhoneCss]: isPhone
  }, props.className)} />;
});
const filterSectionCss = "f7vu4j8";
const FilterSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(filterSectionCss, props.className)} />;
});
const ctaSectionCss = "c950sxb";
const CtaSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(ctaSectionCss, props.className)} />;
});
const MobileHeader = forwardRef(({
  selectedView,
  updateViewParam,
  hideFilter,
  isNotetakerEntitled,
  useResultsCount,
  showPaywall,
  featureKey,
  hasRecapSharingAccess,
  recapScope,
  setRecapScope
}, ref) => {
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  return <HeaderContainer ref={ref} hideFilter={hideFilter}>
      {showPaywall && <Row className={paywallBannerRowCss}>
          <PaywallBanner featureKey={featureKey} />
        </Row>}
      {hasRecapSharingAccess && !hideFilter && <div className={recapScopeTabsWrapCss}>
          <RecapScopeTabs value={recapScope} onChange={setRecapScope} />
        </div>}
      {!hideFilter && <Row>
        <SearchInput />
        <MeetingsFilter location="recaps_page" hiddenFilters={[FilterTypes.BUFFER_VIEW, FilterTypes.STATUS, FilterTypes.ROLE, FilterTypes.TRACKING_SELECT, FilterTypes.NOTETAKER_RECAP]} filterDefaults={{
        period: 'past',
        scheduling_methods: AllSchedulingMethods
      }} useResultsCount={useResultsCount} />
        {isNotetakerEntitled && <MinimalButton as={RouterLink} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH, SETTINGS_MODAL_AI_TAB.NOTETAKER)} aria-label={I18n.t('publisher.personal_settings.navigation.notetaker_settings')}>
            <UiIcon icon={IconSettings} size="small" title="" />
          </MinimalButton>}
        <ViewSwitcher size="standard" value={selectedView} name="meetings-view-switcher" options={[{
        value: 'list',
        icon: <UiIcon icon={IconList} size="small" title="" />,
        text: <VisuallyHidden>{I18n.t('publisher.recaps.navigation.view_switcher.list')}</VisuallyHidden>
      }, {
        value: 'grid',
        icon: <UiIcon icon={IconApps} size="small" title="" />,
        text: <VisuallyHidden>{I18n.t('publisher.recaps.navigation.view_switcher.grid')}</VisuallyHidden>
      }]} onChange={e => updateViewParam(e.target.value)} />
      </Row>}
    </HeaderContainer>;
});
const DesktopHeader = forwardRef(({
  selectedView,
  updateViewParam,
  hideFilter,
  isNotetakerEntitled,
  useResultsCount,
  showPaywall,
  featureKey,
  hasRecapSharingAccess,
  recapScope,
  setRecapScope
}, ref) => {
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  return <HeaderContainer ref={ref} hideFilter={hideFilter}>
      {showPaywall && <Row className={paywallBannerRowCss}>
          <PaywallBanner featureKey={featureKey} />
        </Row>}
      {hasRecapSharingAccess && !hideFilter && <div className={recapScopeTabsWrapCss}>
          <RecapScopeTabs value={recapScope} onChange={setRecapScope} />
        </div>}
      {!hideFilter && <Row>
          <FilterSection>
            <SearchInput />
            <MeetingsFilter location="recaps_page" hiddenFilters={[FilterTypes.BUFFER_VIEW, FilterTypes.STATUS, FilterTypes.ROLE, FilterTypes.TRACKING_SELECT, FilterTypes.NOTETAKER_RECAP]} filterDefaults={{
          period: 'past',
          scheduling_methods: AllSchedulingMethods
        }} useResultsCount={useResultsCount} />
          </FilterSection>
          <CtaSection>
            {isNotetakerEntitled && <MinimalButton as={RouterLink} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH, SETTINGS_MODAL_AI_TAB.NOTETAKER)} aria-label={I18n.t('publisher.personal_settings.navigation.notetaker_settings')}>
                <UiIcon icon={IconSettings} size="small" title="" />
                <span>{I18n.t('publisher.personal_settings.navigation.notetaker_settings')}</span>
              </MinimalButton>}
            <ViewSwitcher value={selectedView} name="meetings-view-switcher" options={[{
          value: 'list',
          icon: <UiIcon icon={IconList} size="small" title="" />,
          text: <VisuallyHidden>{I18n.t('publisher.recaps.navigation.view_switcher.list')}</VisuallyHidden>
        }, {
          value: 'grid',
          icon: <UiIcon icon={IconApps} size="small" title="" />,
          text: <VisuallyHidden>{I18n.t('publisher.recaps.navigation.view_switcher.grid')}</VisuallyHidden>
        }]} onChange={e => updateViewParam(e.target.value)} />
          </CtaSection>
        </Row>}
    </HeaderContainer>;
});
export const SearchAndFilter = forwardRef((props, ref) => {
  const history = useHistory();
  const isPhone = useDeviceType('isPhone');
  const {
    isEntitled: isNotetakerEntitled
  } = useNotetakerEntitled();
  const {
    recapScope,
    setRecapScope,
    hasRecapSharingAccess,
    ...restProps
  } = props;
  const useResultsCount = useMemo(() => createUseResultsCount(useGetMeetingRecapsQuery, hasRecapSharingAccess ? recapScope : MEETINGS_RECAP_SCOPE.ALL_RECAPS), [hasRecapSharingAccess, recapScope]);
  const updateViewParam = view => {
    const params = new URLSearchParams(history.location.search);
    params.set('view', view);
    params.delete('selected');
    params.delete('share_id');
    history.replace({
      search: params.toString()
    });
  };
  const featureKey = 'meeting_intelligence_nonfree';
  const showPaywall = false;
  const commonProps = {
    ref,
    useResultsCount,
    updateViewParam,
    showPaywall,
    featureKey,
    hasRecapSharingAccess,
    recapScope,
    setRecapScope,
    isNotetakerEntitled,
    ...restProps
  };
  return isPhone ? <MobileHeader {...commonProps} /> : <DesktopHeader {...commonProps} />;
});

require("./search_and_filter.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./search_and_filter.js");